import { createContext, useCallback, useContext, useEffect, useMemo, useReducer, useState } from 'react'
import { calcDiscount } from '../config/discountCodes.js'
import { checkoutConfig } from '../config/checkout.js'
import { api } from '../lib/api.js'
import { takeReferralCode } from '../lib/customerApi.js'

const STORAGE_KEY = 'lookslab_cart'

function makeLineId(productId, variantId) {
  return `${productId}__${variantId}`
}

function calcShipping(subtotal) {
  const { freeThreshold, standardRate } = checkoutConfig.shipping
  return subtotal >= freeThreshold ? 0 : standardRate
}

const initialState = {
  items: [],
  discountCode: null,
  discountError: null,
}

function reducer(state, action) {
  switch (action.type) {
    case 'HYDRATE':
      return { ...state, ...action.payload }

    case 'ADD_ITEM': {
      const { item } = action
      const existing = state.items.find((i) => i.lineId === item.lineId)
      if (existing) {
        return {
          ...state,
          items: state.items.map((i) =>
            i.lineId === item.lineId ? { ...i, qty: i.qty + item.qty } : i,
          ),
        }
      }
      return { ...state, items: [...state.items, item] }
    }

    case 'REMOVE_ITEM':
      return { ...state, items: state.items.filter((i) => i.lineId !== action.lineId) }

    case 'UPDATE_QTY':
      if (action.qty < 1) {
        return { ...state, items: state.items.filter((i) => i.lineId !== action.lineId) }
      }
      return {
        ...state,
        items: state.items.map((i) =>
          i.lineId === action.lineId ? { ...i, qty: action.qty } : i,
        ),
      }

    case 'CLEAR_CART':
      return { ...initialState }

    case 'SET_DISCOUNT':
      return { ...state, discountCode: action.code, discountError: null }

    case 'SET_DISCOUNT_ERROR':
      return { ...state, discountCode: null, discountError: action.error }

    case 'REMOVE_DISCOUNT':
      return { ...state, discountCode: null, discountError: null }

    default:
      return state
  }
}

const CartContext = createContext(null)

export function CartProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState)
  const [isOpen, setIsOpen] = useState(false)
  const openCart  = useCallback(() => setIsOpen(true),  [])
  const closeCart = useCallback(() => setIsOpen(false), [])

  // Hydrate cart items from localStorage on mount (cart contents only, not
  // discount). `hydrated` lets pages that redirect on an empty cart (e.g.
  // checkout) wait for this to run first — without it, a hard refresh/direct
  // load of /checkout would see items.length === 0 for one tick (this effect
  // fires after the page's own "redirect if empty" effect, since it's higher
  // in the tree) and bounce home before the saved cart ever loads.
  const [hydrated, setHydrated] = useState(false)
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY)
      if (saved) {
        const parsed = JSON.parse(saved)
        const items = Array.isArray(parsed) ? parsed : (parsed.items ?? [])
        dispatch({ type: 'HYDRATE', payload: { items } })
      }
    } catch { /* ignore */ }
    setHydrated(true)
  }, [])

  // Persist cart items to localStorage on every change — gated on `hydrated`
  // so this can't fire with the pre-hydration empty initialState and clobber
  // a saved cart before the hydrate effect above has had a chance to read it.
  useEffect(() => {
    if (!hydrated) return
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(state.items)) } catch { /* ignore */ }
  }, [state.items, hydrated])

  // ── Derived values ──────────────────────────────────────────

  // lines: items enriched with effectivePrice (applies bulk discount when qty >= threshold)
  const lines = useMemo(
    () => state.items.map((item) => {
      const bd = item.bulkDiscount
      const bulkApplied = !!(bd && item.qty >= bd.threshold)
      const effectivePrice = bulkApplied ? bd.price : item.price
      return { ...item, effectivePrice, bulkApplied }
    }),
    [state.items],
  )

  const subtotal = useMemo(
    () => lines.reduce((sum, l) => sum + l.effectivePrice * l.qty, 0),
    [lines],
  )

  const bulkSavings = useMemo(
    () => lines.reduce((sum, l) => sum + (l.bulkApplied ? (l.price - l.effectivePrice) * l.qty : 0), 0),
    [lines],
  )

  const discountAmount = useMemo(
    () => calcDiscount(state.discountCode, subtotal),
    [state.discountCode, subtotal],
  )
  const shipping = useMemo(
    () => calcShipping(subtotal - discountAmount),
    [subtotal, discountAmount],
  )
  const total = useMemo(
    () => Math.max(0, subtotal - discountAmount + shipping),
    [subtotal, discountAmount, shipping],
  )
  const itemCount = useMemo(
    () => state.items.reduce((n, i) => n + i.qty, 0),
    [state.items],
  )

  // ── Actions ─────────────────────────────────────────────────
  const addItem = useCallback((product, variant, qty = 1) => {
    dispatch({
      type: 'ADD_ITEM',
      item: {
        lineId: makeLineId(product.id, variant.id),
        productId: product.id,
        variantId: variant.id,
        name: product.labelName ?? product.name,
        subtitle: product.subtitle,
        concentration: variant.concentration,
        labelSize: variant.labelSize,
        price: variant.price,
        bulkDiscount: variant.bulkDiscount ?? null,
        qty,
      },
    })
  }, [])

  const removeItem = useCallback((lineId) => dispatch({ type: 'REMOVE_ITEM', lineId }), [])

  const updateQty = useCallback(
    (lineId, qty) => dispatch({ type: 'UPDATE_QTY', lineId, qty }),
    [],
  )

  const clearCart = useCallback(() => dispatch({ type: 'CLEAR_CART' }), [])

  // Async: validates against the Worker API (which reads from D1)
  const applyDiscount = useCallback(async (code) => {
    try {
      const result = await api.validateCode(code, subtotal)
      dispatch({ type: 'SET_DISCOUNT', code: result })
    } catch (e) {
      dispatch({ type: 'SET_DISCOUNT_ERROR', error: e.message ?? 'Invalid code.' })
    }
  }, [subtotal])

  const removeDiscount = useCallback(() => dispatch({ type: 'REMOVE_DISCOUNT' }), [])

  // Auto-apply a referral code picked up from an /r/CODE link — same effect
  // as typing it into the discount box, just automatic so the referrer gets
  // credited whether or not this visitor ever creates an account. Silent on
  // failure (already used, expired) since the visitor never typed anything.
  useEffect(() => {
    if (state.discountCode) return
    const stored = takeReferralCode()
    if (!stored) return
    api.validateCode(stored, subtotal)
      .then((result) => dispatch({ type: 'SET_DISCOUNT', code: result }))
      .catch(() => {})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const value = useMemo(
    () => ({
      items: state.items,
      lines,
      discountCode: state.discountCode,
      discountError: state.discountError,
      subtotal,
      bulkSavings,
      discountAmount,
      shipping,
      total,
      itemCount,
      count: itemCount,
      hydrated,
      isOpen,
      setIsOpen,
      openCart,
      closeCart,
      addItem,
      removeItem,
      updateQty,
      clearCart,
      applyDiscount,
      removeDiscount,
    }),
    [
      state.items, lines, state.discountCode, state.discountError,
      subtotal, bulkSavings, discountAmount, shipping, total, itemCount, hydrated,
      isOpen, openCart, closeCart,
      addItem, removeItem, updateQty, clearCart, applyDiscount, removeDiscount,
    ],
  )

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>
}

export function useCart() {
  const ctx = useContext(CartContext)
  if (!ctx) throw new Error('useCart must be used within CartProvider')
  return ctx
}
